import { IAPIRoutes } from "../../../../config";
import type {
  INeoDashboardRepository,
  NeoDashboard,
  NeoDashboardCsvCellEdit,
  NeoDashboardListResponse,
  NeoDashboardPatchPayload,
  NeoDashboardPayload,
} from "../../../core/ports/driven/neoDashboardRepository";

const env = import.meta.env;

const getApiBaseUrl = (): string =>
  env.VITE_API_GATEWAY_URL_V2 || env.VITE_API_GATEWAY_URL || "";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

export type INeoDashboardRepositoryFactory = ReturnType<
  typeof NeoDashboardRepositoryFactory
>;

export const NeoDashboardRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoDashboardRepository => {
  const { GET, POST, PATCH, DELETE } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const authHeader = async () => ({
    Authorization: `Bearer ${await getAuthenticationToken()}`,
  });

  const listDashboardsAPI = async (
    projectId: number,
    includeSystem = false
  ): Promise<NeoDashboardListResponse> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectDashboards, { project_id: projectId }),
      {
        limit: 100,
        ...(includeSystem ? { include_system: true } : {}),
      },
      await authHeader()
    );

  const createDashboardAPI = async (
    projectId: number,
    payload: NeoDashboardPayload
  ): Promise<NeoDashboard> =>
    POST(
      fillRoute(APIRoutes.apiNeoProjectDashboards, { project_id: projectId }),
      payload,
      await authHeader()
    );

  const getDashboardAPI = async (
    projectId: number,
    dashboardId: number
  ): Promise<NeoDashboard> =>
    GET(
      fillRoute(APIRoutes.apiNeoProjectDashboard, {
        project_id: projectId,
        dashboard_id: dashboardId,
      }),
      {},
      await authHeader()
    );

  const updateDashboardAPI = async (
    projectId: number,
    dashboardId: number,
    payload: NeoDashboardPatchPayload
  ): Promise<NeoDashboard> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectDashboard, {
        project_id: projectId,
        dashboard_id: dashboardId,
      }),
      payload,
      await authHeader()
    );

  const deleteDashboardAPI = async (
    projectId: number,
    dashboardId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectDashboard, {
        project_id: projectId,
        dashboard_id: dashboardId,
      }),
      {},
      await authHeader()
    );
  };

  // Multipart upload — APIProvider does not model FormData bodies, so this
  // adapter owns the raw fetch (still the adapter layer, so no transport
  // detail leaks into the service/page).
  const uploadDashboardCsvAPI = async (
    projectId: number,
    dashboardId: number,
    file: File
  ): Promise<NeoDashboard> => {
    const token = (await getAuthenticationToken()) || "";
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(
      getApiBaseUrl() +
        fillRoute(APIRoutes.apiNeoProjectDashboardCsvs, {
          project_id: projectId,
          dashboard_id: dashboardId,
        }),
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const json = (await response.json()) as { detail?: string };
        if (typeof json.detail === "string") detail = json.detail;
      } catch {
        /* keep generic */
      }
      throw new Error(detail);
    }
    return (await response.json()) as NeoDashboard;
  };

  // CSV content needs the bearer token, so fetch the bytes as text rather than
  // a plain navigation (mirrors NeoWorkflowDocumentsPanel's content fetch).
  const fetchDashboardCsvContentAPI = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ): Promise<string> => {
    const token = (await getAuthenticationToken()) || "";
    const response = await fetch(
      getApiBaseUrl() +
        fillRoute(APIRoutes.apiNeoProjectDashboardCsvContent, {
          project_id: projectId,
          dashboard_id: dashboardId,
          file_id: fileId,
        }),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.text();
  };

  const downloadDashboardCsvXlsxAPI = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ): Promise<Blob> => {
    const token = (await getAuthenticationToken()) || "";
    const response = await fetch(
      getApiBaseUrl() +
        fillRoute(APIRoutes.apiNeoProjectDashboardCsvXlsx, {
          project_id: projectId,
          dashboard_id: dashboardId,
          file_id: fileId,
        }),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.blob();
  };

  // Auto-save: PATCH the CSV's `/content` route with a batch of cell edits.
  // The server applies them, rewrites the CSV to storage, and returns the
  // refreshed dashboard.
  const saveDashboardCsvEditsAPI = async (
    projectId: number,
    dashboardId: number,
    fileId: number,
    edits: NeoDashboardCsvCellEdit[]
  ): Promise<NeoDashboard> =>
    PATCH(
      fillRoute(APIRoutes.apiNeoProjectDashboardCsvContent, {
        project_id: projectId,
        dashboard_id: dashboardId,
        file_id: fileId,
      }),
      { edits },
      await authHeader()
    );

  const deleteDashboardCsvAPI = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ): Promise<void> => {
    await DELETE(
      fillRoute(APIRoutes.apiNeoProjectDashboardCsv, {
        project_id: projectId,
        dashboard_id: dashboardId,
        file_id: fileId,
      }),
      {},
      await authHeader()
    );
  };

  return {
    listDashboardsAPI,
    createDashboardAPI,
    getDashboardAPI,
    updateDashboardAPI,
    deleteDashboardAPI,
    uploadDashboardCsvAPI,
    fetchDashboardCsvContentAPI,
    downloadDashboardCsvXlsxAPI,
    saveDashboardCsvEditsAPI,
    deleteDashboardCsvAPI,
  };
};
